<template>
<div
  class="notifications"
  :style="styles"
>
  <!-- Vue 3's <TransitionGroup> is wired directly here, matching the
       official Vue3 rewrite of this library (@kyvg/vue3-notification) -
       fetched and inspected from its published bundle as an authoritative
       reference, since it's the closest thing to an upstream fix for this
       vendored/hand-ported module. Our original port routed enter/leave
       through an intermediate VelocityGroup.vue/CssGroup.vue component that
       re-emitted Vue's native (el, done) transition hook args as a custom
       {el, complete} event, and omitted the `tag` prop entirely (so
       <transition-group> rendered as a tagless Fragment). The official
       rewrite does neither: it always sets tag="div" and binds
       onEnter/onLeave straight onto <TransitionGroup>, with no extra
       component hop. That indirection is what we removed below - it's the
       most likely source of an intermittent bug seen in the wild (Safari)
       where the velocity `enter` callback ran with `el` undefined, crashing
       in defaults.js's `el.clientHeight` and then again inside
       velocity-animate's internals when the leave animation also received
       no element - not reproducible in Chromium, but eliminated by matching
       upstream's simpler, direct wiring exactly. -->
  <transition-group
    tag="div"
    :css="!isVA"
    :name="animationName"
    @enter="enter"
    @leave="leave"
    @after-leave="clean"
  >
    <div
      v-for="item in active"
      class="notification-wrapper"
      :style="notifyWrapperStyle(item)"
      :key="item.id"
      :data-id="item.id"
    >
      <slot
        name="body"
        :class="[classes, item.type]"
        :item="item"
        :close="() => destroy(item)"
      >
        <!-- Default slot template -->
        <div
          :class="notifyClass(item)"
          @click="click(item)"
        >
          <div
            v-if="item.title"
            class="notification-title"
            v-html="item.title"
          >
          </div>
          <div
            class="notification-content"
            v-html="item.text"
          >
          </div>
              <button v-for="b in item.buttons" @click="b.callback(); destroy(item);">{{ b.title }}</button>
        </div>
      </slot>
    </div>
  </transition-group>
</div>
</template>
<script>
import plugin                         from './index'
import { events }                     from './events'
import { Id, split, listToDirection } from './util'
import defaults                       from './defaults'
import parseNumericValue              from './parser'

const STATE = {
  IDLE: 0,
  DESTROYED: 2
}

const Component = {
  name: 'Notifications',
  props: {
    group: {
      type: String,
      default: ''
    },

    width: {
      type: [Number, String],
      default: 300
    },

    reverse: {
      type: Boolean,
      default: false
    },

    position: {
      type: [String, Array],
      default: () => {
        return defaults.position
      }
    },

    classes: {
      type: String,
      default: 'vue-notification'
    },

    animationType: {
      type: String,
      default: 'css',
      validator (value) {
        return value === 'css' || value === 'velocity'
      }
    },

    animation: {
      type: Object,
      default () {
        return defaults.velocityAnimation
      }
    },

    animationName: {
      type: String,
      default: defaults.cssAnimation
    },

    speed: {
      type: Number,
      default: 300
    },
    /* Todo */
    cooldown: {
      type: Number,
      default: 0
    },

    duration: {
      type: Number,
      default: 3000
    },

    delay: {
      type: Number,
      default: 0
    },

    max: {
      type: Number,
      default: Infinity
    },

    closeOnClick: {
      type: Boolean,
      default: true
    },

    buttons: {
      type: Array,
      default: function() { return [] }
    }
  },
  data () {
    return {
      list: [],
      velocity: plugin.params.velocity
    }
  },
  mounted () {
    events.on('add', this.addItem);
  },
  computed: {
    actualWidth () {
      return parseNumericValue(this.width)
    },
    /**
      * isVelocityAnimation
      */
    isVA () {
      return this.animationType === 'velocity'
    },

    styles () {
      const { x, y } = listToDirection(this.position)
      const width = this.actualWidth.value
      const suffix = this.actualWidth.type

      let styles = {
        width: width + suffix,
        [y]: '0px'
      }

      if (x === 'center') {
        styles['left'] = `calc(50% - ${width/2}${suffix})`
      } else {
        styles[x] = '0px'
      }

      return styles
    },

    active () {
      return this.list.filter(v => v.state !== STATE.DESTROYED)
    },

    botToTop () {
      return this.styles.hasOwnProperty('bottom')
    },
  },
  methods: {
    addItem (event) {
      event.group = event.group || ''

      if (this.group !== event.group) {
        return
      }

      if (event.clean || event.clear) {
        this.destroyAll()
        return
      }

      const duration = typeof event.duration === 'number'
        ? event.duration
        : this.duration

      const speed = typeof event.speed === 'number'
        ? event.speed
        : this.speed

      let { title, text, type, data, buttons } = event

      const item = {
        id: Id(),
        title,
        text,
        type,
        state: STATE.IDLE,
        speed,
        length: duration + 2 * speed,
        data,
        buttons,
        closeOnClick: typeof event.closeOnClick !== 'undefined' ? event.closeOnClick : this.closeOnClick
      }

      if (duration >= 0) {
        item.timer = setTimeout(() => {
          this.destroy(item)
        }, item.length)
      }

      let direction = this.reverse
        ? !this.botToTop
        : this.botToTop

      let indexToDestroy = -1

      if (direction) {
        this.list.push(item)

        if (this.active.length > this.max) {
          indexToDestroy = 0
        }
      } else {
        this.list.unshift(item)

        if (this.active.length > this.max) {
          indexToDestroy = this.active.length - 1
        }
      }

      if (indexToDestroy !== -1) {
        this.destroy(this.active[indexToDestroy])
      }
    },

    notifyClass (item) {
      return [
        'vue-notification-template',
        this.classes,
        item.type
      ]
    },

    notifyWrapperStyle (item) {
      return this.isVA
        ? null
        : {
            transition: `all ${item.speed}ms`
          }
    },

    click(item) {
        if (item.closeOnClick) {
          this.destroy(item)
        }
    },

    destroy (item) {
      // addItem()'s setTimeout callback closes over the plain object it
      // pushed into this.list *before* that push went through Vue 3's
      // reactive Proxy - mutating that raw reference directly bypasses the
      // proxy's set trap, so Vue never sees the change and the item never
      // disappears (this worked under Vue 2's in-place defineProperty
      // reactivity, where there was no raw/proxy distinction). Re-resolve
      // the item from this.list so the mutation goes through the reactive
      // proxy and actually triggers a re-render.
      const target = this.list.find(v => v.id === item.id) || item

      clearTimeout(target.timer)
      target.state = STATE.DESTROYED

      if (!this.isVA) {
        this.clean()
      }
    },

    destroyAll () {
      this.active.forEach(this.destroy)
    },

    getAnimation (index, el) {
      const animation = this.animation[index]

      return typeof animation === 'function'
        ? animation.call(this, el)
        : animation
    },

    // Bound directly to <transition-group>'s native @enter/@leave hooks now
    // (see the template comment above), so these receive Vue's real (el,
    // done) hook arguments positionally instead of an intermediate
    // {el, complete} object re-emitted by a wrapper component.
    enter (el, complete) {
      const animation = this.getAnimation('enter', el)

      this.velocity(el, animation, {
        duration: this.speed,
        complete
      })
    },

    leave (el, complete) {
      let animation = this.getAnimation('leave', el)

      this.velocity(el, animation, {
        duration: this.speed,
        complete
      })
    },

    clean () {
      this.list = this.list.filter(v => v.state !== STATE.DESTROYED)
    }
  }
}

export default Component
</script>
<style>
.notifications {
  display: block;
  position: fixed;
  z-index: 5000;
}

.notification-wrapper {
  display: block;
  overflow: hidden;
  width: 100%;
  margin: 0;
  padding: 0;
}

.notification-title {
  font-weight: 600;
}

.vue-notification-template {
  display: block;	
  box-sizing: border-box;	
  background: white;	
  text-align: left;	
}

.vue-notification {
  display: block;
  box-sizing: border-box;  
  text-align: left;
  font-size: 12px;
  padding: 10px;
  margin: 0 5px 5px;

  color: white;
  background: #44A4FC;
  border-left: 5px solid #187FE7;
}

.vue-notification.warn {
  background: #ffb648;
  border-left-color: #f48a06;
}

.vue-notification.error {
  background: #E54D42;
  border-left-color: #B82E24;
}

.vue-notification.success {
  background: #68CD86;
  border-left-color: #42A85F;
}

.vue-notification button {
  padding: 5px 10px;
  border: 1px solid;
  margin-top: 5px;
  margin-bottom: 5px;
  display: inline-block;
  margin-right: 10px;
}

.vue-notification.success button {
  background-color: #c7edd2;
  border-color: #5bb476;
  color: #3b473f;
}

.vue-notification.warn button {
  background-color: #ffe4bc;
  border-color: #e0a03f;
  color: #4c4438;
}

.vue-notification.error button {
  background-color: #f5bdb9;
  border-color: #c9443a;
  color: #493837;
}

.vn-fade-enter-active, .vn-fade-leave-active, .vn-fade-move  {
  transition: all .5s;
}

.vn-fade-enter-from, .vn-fade-leave-to {
  opacity: 0;
}

</style>
